import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { authAPI } from '../services/auth.api';
import { IconButton } from '@/components/ui';

const UnverifiedEmailBanner: React.FC = () => {
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (!user || user.isEmailVerified || isDismissed) {
    return null;
  }

  const handleResend = async () => {
    setIsResending(true);
    try {
      await authAPI.resendVerification();
    } catch (error) {
      console.error('Resend verification error:', error);
      if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error('Failed to send verification email');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-warning-50 dark:bg-warning-900/20 border-b border-warning-200 dark:border-warning-800/50" role="alert">
      <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1">
            <span className="flex p-2 rounded-lg bg-warning-600 dark:bg-warning-700">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </span>
            <p className="ml-3 font-medium text-warning-900 dark:text-warning-200 text-sm">
              <span className="md:hidden">Please verify your email</span>
              <span className="hidden md:inline">
                Your email address is not verified. Some features may be limited until you verify your email.
              </span>
            </p>
          </div>
          <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleResend}
                disabled={isResending}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-warning-900 dark:text-warning-100 bg-warning-200 dark:bg-warning-700 hover:bg-warning-300 dark:hover:bg-warning-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-warning-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isResending ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-warning-900 dark:border-warning-100 border-t-transparent rounded-full mr-2" aria-hidden="true"></div>
                    Sending...
                  </>
                ) : (
                  'Resend Email'
                )}
              </button>
              <Link
                to="/resend-verification"
                className="text-sm font-medium text-warning-900 dark:text-warning-200 hover:text-warning-700 dark:hover:text-warning-100 underline transition-colors"
              >
                Learn more
              </Link>
            </div>
          </div>
          <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-3">
            <IconButton
              onClick={() => setIsDismissed(true)}
              className="-mr-1 text-warning-900 dark:text-warning-200 hover:bg-warning-100 dark:hover:bg-warning-800/50 focus-visible:ring-warning-500"
              aria-label="Dismiss banner"
              size="sm"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnverifiedEmailBanner;
